import React from 'react';
import { Link as v4Link } from 'react-router-dom';

// Multibrand-UI Components
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Badge from '@zg-rentals/multibrand-ui/lib/Badge';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

// Constants
import ModalConstants from 'app/shared/constants/ModalConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import UserManagementActions from 'app/shared/actions/UserManagementActions';

import pathsManifest from 'app/shared/pathsManifest';

const getRole = user => (user.isAdmin ? 'Company Admin' : 'User');

const roleRenderer = user => <div>{getRole(user)}</div>;

const mobileColumnConfig = {
    user: {
        width: 10,
        dataKey: 'user',
        header: 'User',
        isSortable: true,
    },
    action: {
        width: 2,
        dataKey: 'action',
        header: 'Permissions',
    },
};

const baseDesktopColumnConfig = {
    user: {
        width: 3,
        dataKey: 'user',
        header: 'User',
        isSortable: true,
    },
    role: {
        width: 3,
        dataKey: 'role',
        header: 'Role',
        renderer: roleRenderer,
    },
};

const columnConfigs = {
    getConfigs(isScreenMobile, dispatch, onResendInvite, company, yourToken, activeTab) {
        const desktopColumnConfig = { ...baseDesktopColumnConfig };
        const previewUserProperties = (userBrand, userToken) => {
            const onLoadProperties = () => dispatch(UserManagementActions
                .getCurrentUserProperties(userBrand, userToken));
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.USER_MANAGEMENT_PROPERTY_LIST_MODAL,
                onLoadProperties,
                useModalDialog: false,
            }));
        };

        const previewPendingProperties = (email) => {
            const onLoadProperties = () => dispatch(UserManagementActions
                .getPendingUserProperties(email));
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.USER_MANAGEMENT_PROPERTY_LIST_MODAL,
                onLoadProperties,
                useModalDialog: false,
            }));
        };

        const paths = pathsManifest.getAllPaths();
        const basePath = `${paths.userManagementPage}/${paths.userManagementEditUser}`;
        const toUserManagementPath = (user) => {
            const {
                userBrand,
                userToken,
                email,
                isPending,
            } = user;
            if (isPending) {
                return `${basePath}?email=${email}`;
            }
            return `${basePath}?userBrand=${userBrand}&userToken=${userToken}`;
        };

        // Renderers
        const userRenderer = user => (
            <div>
                <div>
                    <Text emphasize={true} inline={true}>
                        {user.userName}
                    </Text>
                    {user.userToken === yourToken && (
                        <Badge
                            label="You"
                            type="info"
                        />
                    )}
                    {user.isPending && (
                        <Badge
                            label="Pending"
                            type="info"
                        />
                    )}
                    {user.isPendingDelete && (
                        <Badge
                            label="Pending Deactivation"
                            type="info"
                        />
                    )}
                </div>
                {user.email}
                {user.isPending && (
                    <div>
                        <Link onClick={() => onResendInvite(user)}>
                            Resend invite
                        </Link>
                    </div>
                )}
            </div>
        );
        const actionRenderer = user => (user.isPendingDelete ? '-' :
            (
                <Link
                    reactRouterLink={v4Link}
                    to={toUserManagementPath(user)}
                >
                    {user.isCurrentUser ? 'View' : 'Edit'}
                </Link>
            ));
        const propertyRenderer = (user) => {
            if (user.isPendingDelete) {
                return '-';
            }
            const previewProperties = () => {
                if (user.isPending) {
                    previewPendingProperties(user.email);
                } else {
                    previewUserProperties(user.userBrand, user.userToken);
                }
            };
            return (user.propertyCount === 0 ?
                user.propertyCount :
                (
                    <Link onClick={previewProperties}>
                        {user.propertyCount}
                    </Link>
                ));
        };

        if (isScreenMobile) {
            mobileColumnConfig.user.renderer = userRenderer;
            mobileColumnConfig.action.renderer = actionRenderer;
            return Object.values(mobileColumnConfig);
        }
        desktopColumnConfig.user.renderer = userRenderer;
        if (activeTab === 'users') {
            desktopColumnConfig.property = {
                width: 2,
                dataKey: 'propertyCount',
                header: 'Properties',
            };
            desktopColumnConfig.lastLogin = {
                width: 2,
                renderer: (data) => {
                    if (!data.lastActivityTimestamp) {
                        return '-';
                    }
                    const date = new Date(parseFloat(data.lastActivityTimestamp));
                    return date.toLocaleDateString();
                },
                dataKey: 'lastActivityTimestamp',
                header: 'Last Activity',
            };
            desktopColumnConfig.action = {
                width: 2,
                dataKey: 'action',
                header: 'Permissions',
            };
        } else {
            desktopColumnConfig.property = {
                width: 3,
                dataKey: 'propertyCount',
                header: 'Properties',
                align: 'center',
            };
            desktopColumnConfig.action = {
                width: 3,
                dataKey: 'action',
                header: 'Permissions',
                align: 'center',
            };
        }
        desktopColumnConfig.property.renderer = propertyRenderer;
        desktopColumnConfig.action.renderer = actionRenderer;
        return Object.values(desktopColumnConfig);
    },
};

export default columnConfigs;
